import { useState, useCallback } from "react";
import {
  BookRequestPayloadType,
  BookRequestResponseType,
  BookRequestStatus,
  BookRequestPagination,
  ApiBookRequest,
} from "../types/bookRequestTypes";
import { showToast } from "../../../../core/utils/alert";
import {
  useGetBookRequestsQuery,
  useSaveBookRequestMutation,
  useUpdateBookRequestStatusMutation,
  useDeleteBookRequestMutation,
} from "../api/bookRequestApi";

// Helper function to transform API response to client format
const transformBookRequest = (
  apiRequest: ApiBookRequest
): BookRequestResponseType => {
  // Create a standardized student name
  const studentName = `${apiRequest.student.first_name} ${apiRequest.student.last_name}`;

  // Use the original course name instead of modifying it
  const courseName = apiRequest.course.name;

  // Get center name
  const centerName = apiRequest.center.name;

  // Get stage name
  const stageName = apiRequest.stage?.name;

  return {
    id: apiRequest.id,
    student_id: apiRequest.student.id,
    student_name: studentName,
    studentid: `ID-${apiRequest.student.id}`, // Using a generated ID
    course: courseName, // Use the original course name
    stage: stageName, // Use the stage name from API or fallback
    request_date: apiRequest.requested_on,
    status: apiRequest.status,
    center_name: centerName,
    center_id: apiRequest.center.id,
    approved_date:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    approved_by:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_by !== null
          ? apiRequest.reviewed_by
          : undefined
        : undefined,
    rejection_reason:
      apiRequest.status === "rejected"
        ? apiRequest.reviewer_note || undefined
        : undefined,
    rejected_date:
      apiRequest.status === "rejected"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    completion_date:
      apiRequest.status === "completed"
        ? apiRequest.reviewed_on || undefined
        : undefined,
  };
};

export const useBookRequest = (
  params?: BookRequestPagination,
  shouldFetchList: boolean = true
) => {
  const [error, setError] = useState<string | null>(null);

  // Use RTK Query hooks conditionally
  const {
    data: bookRequestsData,
    isLoading: isLoadingRequests,
    refetch: refetchBookRequests,
  } = useGetBookRequestsQuery(params || {}, {
    skip: !shouldFetchList, // Skip the query if we don't need to fetch the list
  });

  const [saveBookRequest] = useSaveBookRequestMutation();
  const [updateStatus] = useUpdateBookRequestStatusMutation();
  const [deleteRequest] = useDeleteBookRequestMutation();

  // Transform API response to client format
  const bookRequests: BookRequestResponseType[] =
    bookRequestsData?.data?.items?.map((item) => transformBookRequest(item)) ||
    [];

  // Get all book requests with optional filtering
  const getAllBookRequests = useCallback(() => {
    if (shouldFetchList) {
      refetchBookRequests();
    }
  }, [refetchBookRequests, shouldFetchList]);

  // Add a new book request
  const addBookRequest = useCallback(
    async (
      payload: BookRequestPayloadType
    ): Promise<{
      success: boolean;
      data?: BookRequestResponseType;
      error?: string;
    }> => {
      try {
        const response = await saveBookRequest(payload).unwrap();

        // Check if response is successful
        if (response.success) {
          const bookRequest = response.data.bookrequest;

          showToast({
            title: "Success",
            text: "Book request created successfully",
            icon: "success",
          });

          return {
            success: true,
            data: transformBookRequest(bookRequest),
          };
        } else {
          throw new Error(response.error || "Failed to create book request");
        }
      } catch (err) {
        const errorMessage = "Failed to create book request";
        showToast({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
        return { success: false, error: errorMessage };
      }
    },
    [saveBookRequest]
  );

  // Update a book request
  const updateBookRequest = useCallback(
    async (
      id: number,
      payload: Partial<BookRequestPayloadType>
    ): Promise<{
      success: boolean;
      data?: BookRequestResponseType;
      error?: string;
    }> => {
      try {
        // This would need to be implemented in the API
        // For now, we'll just update the status if that's what's changing
        if (payload.status) {
          const result = await changeBookRequestStatus(id, payload.status);
          return result;
        }

        return { success: false, error: "Update not implemented in API" };
      } catch (err) {
        const errorMessage = "Failed to update book request";
        showToast({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
        return { success: false, error: errorMessage };
      }
    },
    []
  );

  // Delete a book request
  const deleteBookRequest = useCallback(
    async (id: number): Promise<{ success: boolean; error?: string }> => {
      try {
        await deleteRequest({ id }).unwrap();

        showToast({
          title: "Success",
          text: "Book request deleted successfully",
          icon: "success",
        });

        return { success: true };
      } catch (err) {
        const errorMessage = "Failed to delete book request";
        setError(errorMessage);

        showToast({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });

        return { success: false, error: errorMessage };
      }
    },
    [deleteRequest]
  );

  // Change the status of a book request
  const changeBookRequestStatus = useCallback(
    async (
      id: number,
      status: BookRequestStatus,
      reason?: string
    ): Promise<{
      success: boolean;
      data?: BookRequestResponseType;
      error?: string;
    }> => {
      try {
        const response = await updateStatus({
          id,
          status,
          reviewerNote: reason,
        }).unwrap();

        showToast({
          title: "Success",
          text: `Book request status changed to ${status}`,
          icon: "success",
        });

        return {
          success: true,
          data: transformBookRequest(response.data.bookrequest),
        };
      } catch (err) {
        const errorMessage = "Failed to change book request status";
        setError(errorMessage);

        showToast({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });

        return { success: false, error: errorMessage };
      }
    },
    [updateStatus]
  );

  return {
    bookRequests,
    isLoading: isLoadingRequests,
    error,
    getAllBookRequests,
    addBookRequest,
    updateBookRequest,
    deleteBookRequest,
    changeBookRequestStatus,
    refetch: refetchBookRequests,
  };
};
